import React, { useState, useCallback } from "react";
import { WooProductAttribute } from "../services/woo";
import { showToast } from "../assets/util/showToast";

// Add styles for the hover effect
const styles = `
  .hover-bg-light:hover {
    background-color: #f8f9fa !important;
    transition: background-color 0.15s ease-in-out;
  }
  .cursor-pointer {
    cursor: pointer;
  }
`;

interface ProductAttributesManagerProps {
  productId: number;
  attributes: WooProductAttribute[];
  onAttributesChange: (attributes: WooProductAttribute[]) => void;
  onUpdate?: (
    productId: number,
    attributes: WooProductAttribute[]
  ) => Promise<void>;
}

type AttributeFormData = {
  name: string;
  options: string[];
  visible: boolean;
  variation: boolean;
  position: number;
};

export default function ProductAttributesManager({
  productId,
  attributes,
  onAttributesChange,
  onUpdate,
}: ProductAttributesManagerProps) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState<number | null>(null);
  const [editingOptions, setEditingOptions] = useState<number | null>(null);
  const [editingOptionsInput, setEditingOptionsInput] = useState("");
  const [newAttribute, setNewAttribute] = useState<AttributeFormData>({
    name: "",
    options: [],
    visible: true,
    variation: true,
    position: attributes.length,
  });
  const [optionsInput, setOptionsInput] = useState("");
  const [updating, setUpdating] = useState(false);

  const handleAddAttribute = useCallback(async () => {
    if (!newAttribute.name.trim()) {
      showToast("Attribute name is required", "error");
      return;
    }

    const options = optionsInput
      .split(/[,\n]/)
      .map((opt) => opt.trim())
      .filter(Boolean);

    if (options.length === 0) {
      showToast("At least one option is required", "error");
      return;
    }

    const newAttr: WooProductAttribute = {
      id: 0, // Will be assigned by server
      name: newAttribute.name.trim(),
      options,
      visible: newAttribute.visible,
      variation: newAttribute.variation,
      position: newAttribute.position,
    };

    const updatedAttributes = [...attributes, newAttr];

    try {
      if (onUpdate) {
        setUpdating(true);
        await onUpdate(productId, updatedAttributes);
      }

      onAttributesChange(updatedAttributes);
      setShowAddForm(false);
      setNewAttribute({
        name: "",
        options: [],
        visible: true,
        variation: true,
        position: updatedAttributes.length,
      });
      setOptionsInput("");
      showToast("Attribute added successfully", "success");
    } catch (error: any) {
      showToast(error.message || "Failed to add attribute", "error");
    } finally {
      setUpdating(false);
    }
  }, [
    newAttribute,
    optionsInput,
    attributes,
    onAttributesChange,
    onUpdate,
    productId,
  ]);

  const handleUpdateAttribute = useCallback(
    async (index: number, updates: Partial<WooProductAttribute>) => {
      const updatedAttributes = attributes.map((attr, i) =>
        i === index ? { ...attr, ...updates } : attr
      );

      try {
        if (onUpdate) {
          setUpdating(true);
          await onUpdate(productId, updatedAttributes);
        }

        onAttributesChange(updatedAttributes);
        showToast("Attribute updated", "success");
      } catch (error: any) {
        showToast(error.message || "Failed to update attribute", "error");
      } finally {
        setUpdating(false);
      }
    },
    [attributes, onAttributesChange, onUpdate, productId]
  );

  const handleDeleteAttribute = useCallback(
    async (index: number) => {
      // eslint-disable-next-line no-restricted-globals
      if (!confirm("Are you sure you want to delete this attribute?")) return;

      const updatedAttributes = attributes.filter((_, i) => i !== index);

      try {
        if (onUpdate) {
          setUpdating(true);
          await onUpdate(productId, updatedAttributes);
        }

        onAttributesChange(updatedAttributes);
        showToast("Attribute deleted", "success");
      } catch (error: any) {
        showToast(error.message || "Failed to delete attribute", "error");
      } finally {
        setUpdating(false);
      }
    },
    [attributes, onAttributesChange, onUpdate, productId]
  );

  const updateAttributeLocal = useCallback(
    (index: number, updates: Partial<WooProductAttribute>) => {
      const updatedAttributes = attributes.map((attr, i) =>
        i === index ? { ...attr, ...updates } : attr
      );
      onAttributesChange(updatedAttributes);
    },
    [attributes, onAttributesChange]
  );

  const handleStartEditingOptions = useCallback(
    (index: number) => {
      const attribute = attributes[index];
      setEditingOptions(index);
      setEditingOptionsInput(attribute.options?.join(", ") || "");
    },
    [attributes]
  );

  const handleSaveOptions = useCallback(
    async (index: number) => {
      const options = editingOptionsInput
        .split(/[,\n]/)
        .map((opt) => opt.trim())
        .filter(Boolean);

      if (options.length === 0) {
        showToast("At least one option is required", "error");
        return;
      }

      // Check for duplicate options
      const uniqueOptions = Array.from(new Set(options));
      if (uniqueOptions.length !== options.length) {
        showToast(
          "Duplicate options found. Each option must be unique.",
          "error"
        );
        return;
      }

      try {
        await handleUpdateAttribute(index, { options: uniqueOptions });
        setEditingOptions(null);
        setEditingOptionsInput("");
        showToast(`Updated ${uniqueOptions.length} option(s)`, "success");
      } catch (error) {
        // Error handling is done in handleUpdateAttribute
      }
    },
    [editingOptionsInput, handleUpdateAttribute]
  );

  const handleCancelEditingOptions = useCallback(() => {
    setEditingOptions(null);
    setEditingOptionsInput("");
  }, []);

  return (
    <>
      <style>{styles}</style>
      <div className="card">
        <div className="card-header d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-3">
            <strong>Product Attributes</strong>
            <span className="badge bg-secondary text-white">
              {attributes.length}
            </span>
          </div>

          <button
            className="btn btn-primary btn-sm"
            onClick={() => setShowAddForm(!showAddForm)}
            disabled={updating}
          >
            {showAddForm ? "Cancel" : "+ Add Attribute"}
          </button>
        </div>

        <div className="card-body">
          {/* Add Attribute Form */}
          {showAddForm && (
            <div className="border rounded p-3 mb-4 bg-light">
              <h6 className="mb-3">Add New Attribute</h6>

              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Attribute Name</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. Color, Size, Material"
                    value={newAttribute.name}
                    onChange={(e) =>
                      setNewAttribute((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Position</label>
                  <input
                    type="number"
                    className="form-control"
                    min="0"
                    value={newAttribute.position}
                    onChange={(e) =>
                      setNewAttribute((prev) => ({
                        ...prev,
                        position: Number(e.target.value),
                      }))
                    }
                  />
                </div>

                <div className="col-12">
                  <label className="form-label">Options</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Enter options separated by commas or new lines&#10;e.g. Red, Blue, Green&#10;or&#10;Small&#10;Medium&#10;Large"
                    value={optionsInput}
                    onChange={(e) => setOptionsInput(e.target.value)}
                  />
                  <div className="form-text">
                    Separate options with commas or put each on a new line
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="newAttrVisible"
                      checked={newAttribute.visible}
                      onChange={(e) =>
                        setNewAttribute((prev) => ({
                          ...prev,
                          visible: e.target.checked,
                        }))
                      }
                    />
                    <label
                      className="form-check-label"
                      htmlFor="newAttrVisible"
                    >
                      Visible on product page
                    </label>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="newAttrVariation"
                      checked={newAttribute.variation}
                      onChange={(e) =>
                        setNewAttribute((prev) => ({
                          ...prev,
                          variation: e.target.checked,
                        }))
                      }
                    />
                    <label
                      className="form-check-label"
                      htmlFor="newAttrVariation"
                    >
                      Used for variations
                    </label>
                  </div>
                </div>

                <div className="col-12">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleAddAttribute}
                    disabled={updating}
                  >
                    {updating ? "Adding..." : "Add Attribute"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Attributes List */}
          {attributes.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Name</th>
                    <th>Options</th>
                    <th style={{ width: 100 }}>Position</th>
                    <th style={{ width: 100 }}>Visible</th>
                    <th style={{ width: 100 }}>Variations</th>
                    <th style={{ width: 120 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {attributes.map((attribute, index) => (
                    <tr key={index}>
                      <td>
                        {editingAttribute === index ? (
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={attribute.name}
                            onChange={(e) =>
                              updateAttributeLocal(index, {
                                name: e.target.value,
                              })
                            }
                            onBlur={() => {
                              handleUpdateAttribute(index, {
                                name: attribute.name,
                              });
                              setEditingAttribute(null);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                handleUpdateAttribute(index, {
                                  name: attribute.name,
                                });
                                setEditingAttribute(null);
                              }
                              if (e.key === "Escape") {
                                setEditingAttribute(null);
                              }
                            }}
                            autoFocus
                          />
                        ) : (
                          <span
                            className="cursor-pointer"
                            onClick={() => setEditingAttribute(index)}
                            title="Click to edit"
                          >
                            {attribute.name}
                          </span>
                        )}
                      </td>
                      <td>
                        {editingOptions === index ? (
                          <div className="d-flex flex-column gap-2">
                            <textarea
                              className="form-control form-control-sm"
                              rows={2}
                              value={editingOptionsInput}
                              onChange={(e) =>
                                setEditingOptionsInput(e.target.value)
                              }
                              placeholder="Enter options separated by commas or new lines"
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && e.ctrlKey) {
                                  e.preventDefault();
                                  handleSaveOptions(index);
                                }
                                if (e.key === "Escape") {
                                  e.preventDefault();
                                  handleCancelEditingOptions();
                                }
                              }}
                              autoFocus
                            />
                            <div className="d-flex gap-1">
                              <button
                                type="button"
                                className="btn btn-success btn-sm"
                                onClick={() => handleSaveOptions(index)}
                                disabled={updating}
                              >
                                ✓
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={handleCancelEditingOptions}
                              >
                                ✕
                              </button>
                            </div>
                            <small className="text-muted d-flex justify-content-between">
                              <span>
                                Tip: Press Ctrl+Enter to save, Escape to cancel
                              </span>
                              <span>
                                {
                                  editingOptionsInput
                                    .split(/[,\n]/)
                                    .filter((opt) => opt.trim()).length
                                }{" "}
                                option(s)
                              </span>
                            </small>
                          </div>
                        ) : (
                          <div
                            className="d-flex flex-wrap gap-1 cursor-pointer p-1 rounded hover-bg-light"
                            onClick={() => handleStartEditingOptions(index)}
                            title="Click to edit options"
                          >
                            {attribute.options?.length ? (
                              attribute.options.map((option, optIndex) => (
                                <span
                                  key={optIndex}
                                  className="badge bg-light text-dark"
                                >
                                  {option}
                                </span>
                              ))
                            ) : (
                              <span className="text-muted small">
                                No options - click to add
                              </span>
                            )}
                            <i
                              className="fa fa-edit text-muted ms-1"
                              style={{ fontSize: "12px" }}
                            ></i>
                          </div>
                        )}
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          min="0"
                          value={attribute.position || 0}
                          onChange={(e) =>
                            updateAttributeLocal(index, {
                              position: Number(e.target.value),
                            })
                          }
                          onBlur={() =>
                            handleUpdateAttribute(index, {
                              position: attribute.position,
                            })
                          }
                        />
                      </td>
                      <td>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={attribute.visible ?? true}
                            onChange={(e) =>
                              handleUpdateAttribute(index, {
                                visible: e.target.checked,
                              })
                            }
                          />
                        </div>
                      </td>
                      <td>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={attribute.variation ?? false}
                            onChange={(e) =>
                              handleUpdateAttribute(index, {
                                variation: e.target.checked,
                              })
                            }
                          />
                        </div>
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <button
                            className="btn btn-outline-primary"
                            onClick={() => handleStartEditingOptions(index)}
                            disabled={updating || editingOptions === index}
                            title="Edit options"
                          >
                            <i className="fa fa-edit"></i>
                          </button>
                          <button
                            className="btn btn-outline-danger"
                            onClick={() => handleDeleteAttribute(index)}
                            disabled={updating}
                            title="Delete attribute"
                          >
                            ✕
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-4">
              <div className="text-muted">
                <div className="fs-1 mb-2">🏷️</div>
                <h6>No attributes yet</h6>
                <p className="small">
                  Add product attributes to create variations with different
                  options like size, color, etc.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
